import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  AbstractControl,
  Validators,
  ValidationErrors
} from '@angular/forms';
import { MatDialog } from '@angular/material';
import { TdLoadingService, TdDialogService } from '@covalent/core';
import { Subscription } from 'rxjs/Subscription';

import { AuthService } from '../../services/auth.service';
import { RulesDialogComponent } from '../rules-dialog/rules-dialog.component';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit, OnDestroy {
  /**
   * Tabs
   */
  tabs: string[] = ['ავტორიზაცია', 'რეგისტრაცია'];

  /**
   * Current tab to show
   */
  currentTab: string;

  /**
   * The login form group
   */
  loginForm: FormGroup;

  /**
   * The registration form group
   */
  registerForm: FormGroup;

  /**
   * The validation form group
   */
  validateForm: FormGroup;
  message: string;
  codeDate: Date;
  /**
   * Subscriptions
   */
  private _subs: Set<Subscription> = new Set();

  /**
   * Error keyword/message map
   */
  static readonly errorMap = {
    pid_exists: 'შეყვანილი პირადი ნომერი უკვე რეგისტრირებულია სისტემაში',
    USERNAME_EXISTS: 'შეყვანილი მომხმარებლის სახელი უკვე არსობობს სისტემაში',
    email_exists: 'შეყვანილი ელ-ფოსტა უკვე რეგისტრირებულია სისტემაში',
    USERNAME_OR_PASSWORD_INCORRECT: 'მომხმარებლის სახელი ან პაროლი არასწორია'
  };

  /**
   * Error form and control map
   */
  static readonly errorFormControlMap = {
    // 'pid_exists': {
    //   formName: 'registerForm',
    //   controlName: 'pid'
    // },

    USERNAME_EXISTS: {
      formName: 'registerForm',
      controlName: 'username'
    },

    // 'email_exists': {
    //   formName: 'registerForm',
    //   controlName: 'email'
    // },

    USERNAME_OR_PASSWORD_INCORRECT: {
      formName: 'loginForm',
      controlName: 'username'
    },

    INVALID_TOKEN: {
      formName: 'validateForm',
      controlName: 'token'
    },

    WAIT_UNTIL_RESEND_DELAY: {
      formName: 'validateForm',
      controlName: 'phone'
    }
  };

  public smsCodeSent: boolean = false;

  /**
   * Constructor
   *
   * @param _fb
   * @param _router
   * @param _activatedRoute
   * @param _httpClient
   * @param _dialog
   * @param _viewContainerRef
   * @param _loadingService
   * @param _dialogService
   * @param _authService
   */
  constructor(
    private _fb: FormBuilder,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _httpClient: HttpClient,
    private _dialog: MatDialog,
    private _viewContainerRef: ViewContainerRef,
    private _loadingService: TdLoadingService,
    private _dialogService: TdDialogService,
    private _authService: AuthService
  ) {
    this.currentTab = this.tabs[0];

    this.loginForm = _fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    this.registerForm = _fb.group({
      phone: ['', Validators.required],
      username: ['', Validators.required],
      password: [
        '',
        Validators.compose([Validators.required, Validators.minLength(6)])
      ],
      passwordConfirmation: [
        '',
        Validators.compose([Validators.required, this.samePassword])
      ]
    });

    this.validateForm = _fb.group({
      phone: ['', Validators.required],
      token: ['', Validators.required],
      userId: ['', Validators.required]
    });
  }

  /**
   * Init
   */
  ngOnInit() {
    this._subs.add(
      this._activatedRoute.queryParams.subscribe((params) => {
        const tabIndex = this.tabs.indexOf(params.tab);
        if (tabIndex >= 0) {
          this.setCurrentTab(this.tabs[tabIndex]);
        }
      })
    );

    Array.prototype.forEach.call(
      ['loginForm', 'registerForm', 'validateForm'],
      (formName: string) => {
        this._subs.add(
          this[formName].valueChanges.subscribe((value) => {
            // this[formName].markAsPristine();
            // this[formName].markAsUntouched();
            Object.keys(AuthComponent.errorFormControlMap)
              .filter(
                (key) =>
                  AuthComponent.errorFormControlMap[key].formName === formName
              )
              .forEach((key) => {
                let { controlName } = AuthComponent.errorFormControlMap[key];
                let control = this[formName].controls[controlName];
                if (control.hasError(key)) {
                  // control.setErrors({ [key]: null });
                  // control.markAsPristine();
                  // control.markAsUntouched();
                  delete control.errors[key];
                  if (Object.keys(control.errors).length === 0) {
                    control.setErrors(null);
                  }
                }
              });
          })
        );
      }
    );
  }

  /**
   * Destroy
   */
  ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Returns errorMap
   */
  get errorMap() {
    return AuthComponent.errorMap;
  }

  /**
   * Password confirmation validator
   *
   * @param control
   */
  samePassword(control: AbstractControl): ValidationErrors | null {
    if (!control || !control.parent) {
      return null;
    }

    if (control.value !== control.parent.get('password').value) {
      return { passwordMismatch: true };
    }

    return null;
  }

  /**
   * Opens alert dialog
   *
   * @param message
   * @param title
   */
  private _openAlert(message: string, title: string = '') {
    return this._dialogService.openAlert({
      message: message,
      viewContainerRef: this._viewContainerRef, //OPTIONAL
      title: title, //OPTIONAL, hides if not provided
      closeButton: 'დახურვა', //OPTIONAL, defaults to 'CLOSE'
      width: '400px' //OPTIONAL, defaults to 400px
    });
  }

  /**
   * Parses an url
   *
   * @param url
   */
  private _parseUrl(url: string): any {
    return jQuery('<a/>').attr('href', url)[0];
  }

  /**
   * Gets query parameters from query string
   *
   * @param str
   */
  private _getQueryParameters(str?: string) {
    if (typeof str !== 'string') str = document.location.search;
    return str
      .replace(/(^\?)/, '')
      .split('&')
      .map(
        function (n) {
          return (n = n.split('=')), (this[n[0]] = n[1]), this;
        }.bind({})
      )[0];
  }

  /**
   * Sets form errors
   *
   * @param errors
   */
  private _handleFormErrors(errors: any[]) {
    errors.forEach((err) => {
      if (AuthComponent.errorFormControlMap[err.keyword]) {
        // Extract formName and controlName
        const { formName, controlName } = AuthComponent.errorFormControlMap[
          err.keyword
        ];

        // Fetch the form
        const form: FormGroup = this[formName];

        // Check for form and control existance
        if (form && form.contains(controlName)) {
          // Set form control errors
          form.controls[controlName].setErrors({ [err.keyword]: true });
        }
      }
    });
  }

  /**
   * Sets current tab
   *
   * @param tab
   */
  setCurrentTab(tab: string) {
    this.currentTab = tab;

    if (this.currentTab === this.tabs[1]) {
      this._dialog
        .open(RulesDialogComponent, {})
        .afterClosed()
        .subscribe((result) => {
          if (result) return;
          this.currentTab = this.tabs[0];
        });
    }
  }

  /**
   * Validates PID
   *
   * @param pid
   */
  isValidPid(pid: string) {
    return /^\d{11}$/.test(pid);
  }

  /**
   * Validates document number
   *
   * @param documentNumber
   */
  isValidDocumentNumber(documentNumber: string) {
    return /^\d{2}[A-Z]{2}\d{5}|[ა-ჰ]\d{7}$/.test(documentNumber);
  }

  /**
   * Retrieves person by pid and document number
   */
  getPersonByPidAndDocumentNumber() {
    if (
      this.registerForm.controls['pid'].valid &&
      this.registerForm.controls['documentNumber'].valid
    ) {
      const { pid, documentNumber } = this.registerForm.value;
      const data = { pid, documentNumber };
      this._loadingService.register();
      this._httpClient
        .post('/api/auth/getPersonByPidAndDocumentNumber', { data })
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe((res) => {
          if (res['result'].data) {
            this.registerForm.patchValue(res['result'].data[0]);
          }
        });
    }
  }

  /**
   * Login
   *
   * @param event
   */
  login(event: Event) {
    // this.loginForm.reset();
    // this.loginForm.markAsPristine();
    // this.loginForm.markAsUntouched();
    if (this.loginForm.valid) {
      this._loadingService.register();
      this._authService
        .login(this.loginForm.value)
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe(
          (res: any) => {
            if (res.errors) {
              this._handleFormErrors(res.body.result.errors);
            } else if (res.body.result.data) {
              this._authService.employeeCheck().subscribe(() => {
                // Get the redirect URL from our auth service
                // If no redirect has been set, use the default
                let redirect = this._authService.redirectUrl
                  ? this._authService.redirectUrl
                  : '/';

                if (redirect === '/') {
                  if (
                    this._activatedRoute.snapshot.queryParams['redirectUrl']
                  ) {
                    redirect = decodeURIComponent(
                      this._activatedRoute.snapshot.queryParams['redirectUrl']
                    );
                  }
                }

                const parsed = this._parseUrl(redirect),
                  queryParams = this._getQueryParameters(parsed.search);

                // Redirect the user
                this._router
                  .navigate([parsed.pathname], { queryParams })
                  .then(() => location.reload());
              });
            }
          },
          (error) => {
            this._openAlert(error.statusText);
          }
        );
    }
  }

  /**
   * Registration
   *
   * @param event
   */
  register(event: Event) {
    // this.registerForm.reset();
    // this.registerForm.markAsPristine();
    // this.registerForm.markAsUntouched();
    this.smsCodeSent = false;
    if (this.registerForm.valid) {
      const data = Object.assign(
        { link: `${location.protocol}//${location.host}/auth/verify/` },
        this.registerForm.value
      );

      this._loadingService.register();
      this._httpClient
        .post('/api/um/user', { data })
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe(
          (res: any) => {
            if (res.result.errors) {
              this._handleFormErrors(res.result.errors);
            } else if (res.result.data) {
              this.validateForm
                .get('phone')
                .setValue(this.registerForm.get('phone').value);
              this.validateForm.get('userId').setValue(res.result.data.id);
              this.smsCodeSent = true;
              this.codeDate = new Date();
              this.message = 'ვერიფიკაციის კოდი გაგზავნილია მითითებულ ნომერზე';
              // this._openAlert('რეგისტრაციის დასასრულებლად გთხოვთ მიჰყვეთ მითითებულ ელ. ფოსტაზე გამოგზავნილ ბმულს.').afterClosed().subscribe(() => {
              //   this._router.navigate(['/auth']);
              // });
            }
          },
          (error) => {
            this._openAlert(error.statusText);
          }
        );
    }
  }

  validate(event: Event) {
    this.message = null;

    if (this.validateForm.valid) {
      const data = this.validateForm.value;
      this._loadingService.register();
      this._httpClient
        .post('/api/um/user/activate', { data })
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe(
          (res: any) => {
            if (res.errors) {
              this._handleFormErrors(res.result.errors);
            } else if (res.result.data) {
              this._openAlert('რეგისტრაცია წარმატებით დასრულდა')
                .afterClosed()
                .subscribe(() => {
                  this.currentTab = this.tabs[0];
                });
            }
          },
          (error) => {
            this._openAlert(error.statusText);
          }
        );
    }
  }

  resend() {
    this.message = null;
    const data = this.validateForm.value;
    this._loadingService.register();
    this._httpClient
      .get(`/api/um/user/token/resend/${data.userId}`)
      .finally(() => {
        this._loadingService.resolve();
      })
      .subscribe((res: any) => {
        if (res.result.errors) {
          this._handleFormErrors(res.result.errors);
        } else if (res.result.data) {
          this.message = 'ვერიფიკაციის კოდი გაგზავნილია მითითებულ ნომერზე';
        }
      });
  }

  getDelayMessage() {
    return (
      'სცადეთ ' +
      Math.round(
        (60000 - (new Date().getTime() - this.codeDate.getTime())) / 1000
      ) +
      ' წამის შემდეგ'
    );
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/components/auth/auth.component.ts